import { useContext, createContext, Dispatch } from 'react';
import { Action } from 'client/reducers/recruitents/RecruitmentList';
import { RecruitmentSearchInput } from 'client/generated';
import { format } from 'date-fns';

type DisplayMode =
  | 'list'
  | 'search-modal'
  | 'area-modal'
  | 'license-modal'
  | 'facility-feature-modal'
  | 'job-benefit-modal'
  | 'job-task-modal'
  | 'sort-setting-modal'
  | 'working-hour-option-modal';

// eslint-disable-next-line @typescript-eslint/ban-types
export type ContextState = {
  searchQueryParams: Required<Omit<RecruitmentSearchInput, 'pagingCursor'>>;
  currentDateText: string;
  ui: {
    displayMode: DisplayMode;
    modeHistory: DisplayMode[];
  };
};

type RecruitmentListContextType = ContextState & {
  dispatch: Dispatch<Action>;
};

export const createInitialState = (currentDate: Date, areaIds: string[] | null): ContextState => {
  const currentDateText = format(currentDate, 'yyyy-MM-dd');
  return {
    searchQueryParams: {
      date: currentDateText,
      areaIds: null,
      licenseIds: null,
      jobBenefitIds: null,
      workingHourOptionIds: null,
      maximumWorkingHours: null,
      jobTaskIds: null,
      maximumDailyWage: null,
      minimumDailyWage: null,
      facilityFeatureIds: null,
      sortOptionId: null,
      onlyRecruiting: null,
    },
    // クエリのdateに合わせており、「日付」だけほしい
    currentDateText: currentDateText,
    // ワーカーWebの募集検索は都道府県選択が最低でも1つは必要なので、都道府県がない場合は都道府県選択を強制
    ui:
      areaIds && areaIds.length > 0
        ? {
            displayMode: 'list',
            modeHistory: ['list'],
          }
        : {
            displayMode: 'area-modal',
            modeHistory: ['list', 'area-modal'],
          },
  };
};

// 検索系の各種コンポーネントがRecruitmentListContextやuseRecruitmentListContextを使うための便宜上の措置
// 実際に募集検索画面の初期状態はdefaultStateがそのまま使われず、
// URLのクエリパラメータからページ着地時の検索条件のContextStateを復元して、
// useReducerに復元したContextStateを食わせる
// 詳細は src/pages/recruitment.tsx を参照
export const defaultState = createInitialState(new Date(), null);

export const RecruitmentListContext = createContext<RecruitmentListContextType>({
  ...defaultState,
  dispatch: (): void => {},
});

export const useRecruitmentListContext = () => useContext(RecruitmentListContext);
